import React from 'react';
import { useMutation } from '@apollo/client';
import { Formik, FormikProps } from 'formik';
import { useIntl } from 'react-intl';

import { City } from 'app/shared/typings';
import { CreateCustomerLeadApiResponse } from 'app/shared/graphql/customerLeads/mutationHooks';
import { CREATE_CUSTOMER_LEAD } from 'app/shared/graphql/customerLeads/mutations';
import { Box, BoxProps } from 'app/shared/components/atoms';
import { useCreateCustomerLead } from 'app/fan/utils/useCreateCustomerLead';

import { Content } from './Content';
import { NewsletterValidationSchema } from './NewsletterValidationSchema';
import { OnNewsletterSuccess } from './Success';

interface Props extends BoxProps {
  withCitySelector: boolean;
  formContext: string;
  useTwoColumnLayout?: boolean;
  ctaText?: string;
  fullWidthCta?: boolean;
  onSuccess?: OnNewsletterSuccess;
}

export const NewsletterForm: React.FC<React.PropsWithChildren<Props>> = ({
  withCitySelector,
  formContext,
  useTwoColumnLayout = false,
  ctaText,
  fullWidthCta = false,
  onSuccess,
  ...props
}) => {
  const intl = useIntl();
  const [createCustomerLead] =
    useMutation<CreateCustomerLeadApiResponse>(CREATE_CUSTOMER_LEAD);
  const { handleSubmit } = useCreateCustomerLead();

  const initialValues: {
    email: string;
    city: City | undefined;
  } = { email: '', city: undefined };

  return (
    <Box flexDirection="column" data-qaid="newsletter-form" {...props}>
      <Formik
        initialValues={initialValues}
        validationSchema={NewsletterValidationSchema({
          intl,
          withCity: withCitySelector,
        })}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values, { setSubmitting, setStatus }) =>
          handleSubmit({
            values,
            setSubmitting,
            setStatus,
            intl,
            createCustomerLead,
          })
        }
      >
        {(formikProps: FormikProps<any>) => {
          const { values, status } = formikProps;
          const isSuccess = withCitySelector
            ? !!values.city && status?.success
            : status?.success;

          return (
            <Content
              formikProps={formikProps}
              isSuccess={isSuccess}
              formContext={formContext}
              withCitySelector={withCitySelector}
              useTwoColumnLayout={useTwoColumnLayout}
              fullWidthCta={fullWidthCta}
              ctaText={ctaText}
              onSuccess={onSuccess}
            />
          );
        }}
      </Formik>
    </Box>
  );
};
